<script>
import { GlobalBus } from '@services/bus'
import { MODAL, ACTION } from '@constants/Text'
import apiAtendimentoConexao from '@modules/settings/api/apiAtendimentoConexao'
import {
  STATUS_COLORS,
  STATUS_LABELS,
} from '@modules/settings/constants/Atendimento'
import {
  CONEXOES_CONFIG_ROUTE_ATENDIMENTO,
  CONEXOES_CONFIG_ROUTE_SOLICITACOES,
} from '@modules/settings/constants/Text'
import { podeAcessarConexoesAtendimento } from '@modules/settings/utils/atendimento-conexoes-acesso'
import { requireSolicitacoesInternal } from '@modules/settings/routes'
import { ICONS } from '@modules/atendimento/constants/Text'

// HTTP 200-always contract: status real vem no body. 409 sinaliza que a
// remoção na Evolution falhou (instância presa) e que é possível forçar a
// remoção apenas na plataforma (`value.can_force`).
const STATUS_CONFLICT = 409
const ACTION_DELETED = 'deleted'

const TIPO_INDIVIDUAL = 'individual'
const STATUS_CONECTADO = 'conectado'
const STATUS_LABEL_SEM_CONEXAO = 'Sem conexão'
const STATUS_COLOR_SEM_CONEXAO = '#9E9E9E'
const CONEXAO_SEM_NOME = 'Conexão sem nome'

// Rotas de Configurações alcançáveis pelo atalho do menu, com o guard que
// protege CADA UMA. O atalho tem que responder o guard do DESTINO: perguntar
// só "é gestor" acertava o Atendimento e errava Solicitações, cujo guard exige
// também escritório Interno AAWZ — gestor de escritório comum via o item e
// caía em 404.
const CONFIG_ROUTE_GUARDS = {
  [CONEXOES_CONFIG_ROUTE_ATENDIMENTO]: ({ user, show }) =>
    podeAcessarConexoesAtendimento(user, show),
  [CONEXOES_CONFIG_ROUTE_SOLICITACOES]: requireSolicitacoesInternal,
}

// Textos do fluxo de remoção. Centralizados para evitar magic strings
// espalhadas e manter os acentos PT-BR corretos.
const TEXTS = {
  removerTitle: 'Remover conexão',
  removerAlvoPrefixo: 'Conexão selecionada:',
  removerBody:
    'ATENÇÃO: Esta ação vai remover a conexão e APAGAR PERMANENTEMENTE todo o histórico de conversas, mensagens e arquivos. Essa ação NÃO pode ser desfeita. Tem certeza?',
  removerConfirm: 'Remover',
  forcarTitle: 'Forçar remoção',
  forcarComplemento:
    'Forçar a remoção vai apagar a conexão apenas na plataforma, deixando a instância órfã no servidor. Deseja continuar?',
  forcarConfirm: 'Forçar remoção',
  cancelar: 'Cancelar',
  erroInesperado: 'Erro inesperado',
}

export default {
  name: 'ConexaoActionsMenu',
  props: {
    conexoes: {
      type: Array,
      default() {
        return []
      },
    },
    // Estado de loading do refresh-chats-list, controlado pelo pai
    // (view-atendimento / atendimento-popup via ConversationList).
    // Quando `true`, o item "Recarregar conversas" entra em loading e
    // não responde a cliques (guard de duplo-clique).
    refreshing: {
      type: Boolean,
      default: false,
    },
    // Subconjunto de `conexoes` filtrado por `status === 'conectado'`.
    // Recebido pronto do pai (ConversationList) para evitar duplicar
    // a lógica de filtragem. Usado para gating do item refresh: se
    // não há conexão conectada visível, o item fica desabilitado.
    connectedConexoes: {
      type: Array,
      default() {
        return []
      },
    },
    // Seams de rota/eventos — defaults = atendimento; solicitacoes injeta os
    // seus (chaves MODAL/ACTION disjuntas + rota de configurações própria).
    configRoute: {
      type: String,
      default: CONEXOES_CONFIG_ROUTE_ATENDIMENTO,
    },
    termosKey: {
      type: String,
      default: MODAL.ATENDIMENTO_TERMOS,
    },
    qrcodeKey: {
      type: String,
      default: MODAL.ATENDIMENTO_QRCODE_CONEXAO,
    },
    conexaoAtualizadaKey: {
      type: String,
      default: ACTION.ATENDIMENTO_CONEXAO_ATUALIZADA,
    },
  },
  data() {
    return {
      loading: false,
      menuOpen: false,
    }
  },
  computed: {
    icons() {
      return ICONS
    },
    currentUser() {
      return this.$currentUser() || {}
    },
    isGestor() {
      return !!this.currentUser.IS_GESTOR
    },
    // Atalho para as Configurações de conexões. Avalia o guard do DESTINO,
    // sempre o mesmo que protege a rota — menu mais frouxo que o guard vira
    // link para 404, mais apertado vira tela inalcançável (settings/CLAUDE.md
    // §14.6). Sem destino conhecido, default-closed.
    //
    // ★ O papel do usuário NÃO é testado aqui, nem por early return: cada
    // guard já resolve o que fazer com gestor e não-gestor (Atendimento libera
    // os dois com a feature; Solicitações exige gestor + Interno AAWZ). O
    // `$access` é lido ANTES de qualquer ramo, senão o Vue não o registra como
    // dependência e o computed congela no primeiro render (§14.5).
    canGoToConfig() {
      const show = this.$access
      const user = this.currentUser
      // Decisão por identificador de rota, nunca por rótulo (§14.9).
      // `Object.hasOwn` e obrigatório: sem ele, `configRoute` valendo
      // 'toString'/'valueOf' acha uma funcao na cadeia de prototipo e o atalho
      // abriria para rota inexistente — furando o default-closed declarado logo
      // abaixo.
      const guard = Object.hasOwn(CONFIG_ROUTE_GUARDS, this.configRoute)
        ? CONFIG_ROUTE_GUARDS[this.configRoute]
        : null
      if (!guard) return false
      return Boolean(guard({ user, show }))
    },
    // Backend (`listarConexoes`) já filtra as conexões visíveis ao
    // profissional logado via `session['id_profissional']` — as
    // `individual` desta lista são as dele. Com [US-2] ele passa a ter N,
    // então NÃO existe mais "a conexão dele": o menu lista todas e cada
    // ação carrega o próprio alvo.
    conexoesIndividuais() {
      const list = this.conexoes || []
      return list.filter((c) => Boolean(c) && c.tipo === TIPO_INDIVIDUAL)
    },
    temMultiplasIndividuais() {
      return this.conexoesIndividuais.length > 1
    },
    // View-model do bloco de cada conexão: mantém o template livre de
    // chamada de função (regra do repo) e carrega o alvo explícito de cada
    // ação. Só entra aqui o que template ou modal consomem. `nomeExibido` só
    // aparece quando há ambiguidade — com uma única conexão o menu fica
    // idêntico ao de hoje.
    conexoesIndividuaisView() {
      const multiplas = this.temMultiplasIndividuais
      return this.conexoesIndividuais.map((c) => {
        const status = c.status || ''
        const nome = c.nome || CONEXAO_SEM_NOME
        return {
          id: c.id,
          conectado: status === STATUS_CONECTADO,
          statusLabel: STATUS_LABELS[status] || STATUS_LABEL_SEM_CONEXAO,
          statusColor: STATUS_COLORS[status] || STATUS_COLOR_SEM_CONEXAO,
          nomeExibido: multiplas ? nome : '',
        }
      })
    },
    hasIndividual() {
      return this.conexoesIndividuais.length > 0
    },
    // O ativador ⋮ usa este computed. Com ZERO conexões individuais o menu
    // ainda tem o que oferecer ao não-gestor: o atalho para as Configurações —
    // e é exatamente quem PRECISA dele, porque é por lá que a primeira conexão
    // nasce. Desabilitar aqui tornaria inalcançável o item que o guard
    // `requireConexoesAtendimento` acabou de liberar (settings/CLAUDE.md
    // §14.6: menu e guard são duas fontes que precisam concordar).
    //
    // ★ `canGoToConfig` (que lê `$access`) é resolvido ANTES do teste de
    // papel: com `isGestor` num early return, o Vue não registraria `$access`
    // como dependência e o computed congelaria no primeiro render (§14.5).
    disabled() {
      const podeIrParaConfig = this.canGoToConfig
      if (this.isGestor) return false
      return !this.hasIndividual && !podeIrParaConfig
    },
    // Item "Recarregar conversas" só faz sentido se há ao menos uma
    // conexão conectada visível ao usuário. O próprio TEXTO do item troca
    // dinamicamente para "Nenhuma conexão conectada para atualizar"
    // quando desabilitado (UX inline — não usamos tooltip aqui porque
    // tooltip dentro de v-list-item conflita com o close-on-content-click
    // do v-menu pai). `refreshing` também desabilita (anti duplo-clique).
    canRefreshChats() {
      return (this.connectedConexoes || []).length > 0 && !this.refreshing
    },
    refreshChatsLabel() {
      if ((this.connectedConexoes || []).length === 0) {
        return 'Nenhuma conexão conectada para atualizar'
      }
      return 'Recarregar conversas'
    },
  },
  methods: {
    goToConfig() {
      this.$router.push({ name: this.configRoute })
    },
    // Re-emite o evento para o pai (ConversationList) que por sua vez
    // re-emite para o handler do pai-do-pai (view-atendimento /
    // atendimento-popup). Não dispara nada se o item está desabilitado
    // ou já está em loading (defesa em profundidade — o item já tem
    // `:disabled` no template).
    onRefreshChats() {
      if (!this.canRefreshChats) return
      this.$emit('refresh-chats-list')
    },
    // Todas as ações recebem a conexão-alvo do próprio item de menu
    // (view-model de `conexoesIndividuaisView`). Sem alvo, viram no-op: é o
    // que garante que nada aja "na primeira da lista" quando há N.
    openQrCode(conexao) {
      if (!conexao || !conexao.id) return
      const idConexao = conexao.id
      // Profissional precisa aceitar os Termos de Uso do módulo
      // Atendimento antes de parear o WhatsApp. O callback só é
      // invocado se o aceite for concluído com sucesso; se o user
      // cancelar o modal de termos, o QR Code não abre.
      GlobalBus.$emit(this.termosKey, {
        callback: () => {
          GlobalBus.$emit(this.qrcodeKey, {
            id_conexao: idConexao,
          })
        },
      })
    },
    async runAction(apiFn, action, conexao) {
      if (this.loading) return
      if (!conexao || !conexao.id) return
      this.loading = true
      try {
        const r = await apiFn({ id_conexao: conexao.id })
        this.$showSnack({
          text: (r && r.message) || 'Erro inesperado',
          color: r && r.status === 200 ? 'success' : 'error',
          button: true,
        })
        if (r && r.status === 200) {
          this.$emit('conexao-updated', { action })
          // Canal GlobalBus: cobre o caso do popup de atendimento, onde o
          // painel (e este componente dentro dele) é desmontado enquanto
          // modais v-dialog estão abertos — o listener no root do popup
          // sobrevive ao unmount e dispara o refresh das conexões.
          GlobalBus.$emit(this.conexaoAtualizadaKey, { action })
        }
      } finally {
        this.loading = false
      }
    },
    onReiniciar(conexao) {
      this.runAction(
        apiAtendimentoConexao.reiniciar.bind(apiAtendimentoConexao),
        'restarted',
        conexao
      )
    },
    onDesconectar(conexao) {
      this.runAction(
        apiAtendimentoConexao.desconectar.bind(apiAtendimentoConexao),
        'disconnected',
        conexao
      )
    },
    onDesativar(conexao) {
      this.runAction(
        apiAtendimentoConexao.desconectar.bind(apiAtendimentoConexao),
        'deactivated',
        conexao
      )
    },
    // A remoção é irreversível: quando o profissional tem mais de uma
    // conexão, o modal nomeia qual será apagada. Com uma só, o texto segue
    // o de hoje.
    textoRemocao(conexao) {
      const nome = (conexao && conexao.nomeExibido) || ''
      if (!nome) return TEXTS.removerBody
      return `${TEXTS.removerAlvoPrefixo} ${nome}. ${TEXTS.removerBody}`
    },
    // 1º passo: confirmação padrão de remoção (danger). Ao confirmar,
    // dispara a remoção SEM force; o 409 é tratado em `performDelete`,
    // abrindo o 2º modal (forçar remoção).
    onRemoverPermanentemente(conexao) {
      if (!conexao || !conexao.id) return
      this.$showModal({
        title: TEXTS.removerTitle,
        text: this.textoRemocao(conexao),
        type: 'danger',
        v2: true,
        center: true,
        buttons: [
          { text: TEXTS.cancelar },
          {
            text: TEXTS.removerConfirm,
            action: () => this.performDelete({ force: false, conexao }),
          },
        ],
      })
    },
    // Reaproveita o mesmo contrato de refresh do `runAction`: em sucesso,
    // emite `conexao-updated` e propaga via GlobalBus (cobre o popup, cujo
    // painel pode ser desmontado enquanto o modal está aberto).
    onDeleteSuccess() {
      this.$emit('conexao-updated', { action: ACTION_DELETED })
      GlobalBus.$emit(this.conexaoAtualizadaKey, {
        action: ACTION_DELETED,
      })
    },
    // 2º passo: modal de forçar remoção, aberto somente quando o backend
    // responde 409 (instância presa na Evolution). Usa a mensagem do
    // backend + complemento claro sobre a instância ficar órfã.
    showForceDeleteModal(backendMessage, conexao) {
      const text = backendMessage
        ? `${backendMessage} ${TEXTS.forcarComplemento}`
        : TEXTS.forcarComplemento
      this.$showModal({
        title: TEXTS.forcarTitle,
        text,
        type: 'danger',
        v2: true,
        center: true,
        buttons: [
          { text: TEXTS.cancelar },
          {
            text: TEXTS.forcarConfirm,
            action: () => this.performDelete({ force: true, conexao }),
          },
        ],
      })
    },
    // Resposta sinaliza "instância presa, mas dá pra forçar" — 409 do
    // backend OU flag explícita `value.can_force`.
    isForceableConflict(r) {
      if (!r) return false
      if (r.status === STATUS_CONFLICT) return true
      return !!(r.value && r.value.can_force)
    },
    // Snack padrão da remoção a partir da resposta (contrato HTTP 200).
    feedbackDelete(r) {
      const success = !!(r && r.status === 200)
      this.$showSnack({
        text: (r && r.message) || TEXTS.erroInesperado,
        color: success ? 'success' : 'error',
        button: true,
      })
      if (success) this.onDeleteSuccess()
    },
    // Executa a remoção e interpreta a resposta sob o contrato HTTP 200:
    //   200 → sucesso (snack + refresh)
    //   409 (ou value.can_force) → abre o modal de forçar remoção (sem force)
    //   demais → erro comum (snack com a mensagem do backend)
    async performDelete({ force = false, conexao = null } = {}) {
      if (this.loading) return
      if (!conexao || !conexao.id) return
      this.loading = true
      try {
        const r = await apiAtendimentoConexao.deletar({
          id_conexao: conexao.id,
          force,
        })
        if (!force && this.isForceableConflict(r)) {
          this.showForceDeleteModal(r && r.message, conexao)
          return
        }
        this.feedbackDelete(r)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    right
    :nudge-bottom="20"
    :nudge-right="20"
    :close-on-content-click="true"
    content-class="conexao-actions-menu"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-tooltip top>
        <template v-slot:activator="{ on: onTooltip, attrs: attrsTooltip }">
          <!-- attrs + on SEMPRE juntos: sem os attrs o Vuetify não aplica
               `aria-describedby` e o tooltip some para leitor de tela. -->
          <span v-bind="attrsTooltip" v-on="onTooltip">
            <v-btn
              data-test="conexao-actions-trigger"
              icon
              primary
              :disabled="disabled"
              :loading="loading"
              v-bind="attrs"
              v-on="on"
            >
              <icon-svg :name="icons.dotsVertical" size="16" color="primary" />
            </v-btn>
          </span>
        </template>
        <span>Ações</span>
      </v-tooltip>
    </template>

    <v-list class="az-list-no-icon-spacing py-1" dense>
      <!-- Item compartilhado: Recarregar conversas. Visível para gestor
           e para profissional com conexão individual. Dispara refresh
           leve da lista de conversas/contatos (sem mensagens) — popular
           grupos novos / corrigir lista incompleta. -->
      <v-list-item
        v-if="isGestor || hasIndividual"
        :disabled="!canRefreshChats"
        data-test="menu-refresh-chats-list"
        @click="onRefreshChats"
      >
        <v-list-item-icon>
          <v-progress-circular
            v-if="refreshing"
            :size="16"
            :width="2"
            indeterminate
            color="#8e8e8e"
          />
          <icon-svg v-else :name="icons.refresh" size="16" color="#8e8e8e" />
        </v-list-item-icon>
        <v-list-item-title>{{ refreshChatsLabel }}</v-list-item-title>
      </v-list-item>

      <v-divider v-if="isGestor || hasIndividual" />

      <!-- Atalho para as Configurações de conexões. Não é mais gestor-only:
           segue a mesma regra do guard de rota (`canGoToConfig`). -->
      <v-list-item
        v-if="canGoToConfig"
        data-test="menu-goto-config"
        @click="goToConfig"
      >
        <v-list-item-icon>
          <icon-svg :name="icons.settings" size="16" color="#8e8e8e" />
        </v-list-item-icon>
        <v-list-item-title> Ir para Configurações </v-list-item-title>
      </v-list-item>

      <!-- Um bloco por conexão individual do profissional: com mais de uma, o
           CABEÇALHO do bloco nomeia a conexão e cada ação leva a própria
           conexão-alvo. O nome não se repete nos rótulos das ações: ele aceita
           100 chars (`modal-nova-conexao`) e o `.v-list-item__title` é
           nowrap+ellipsis, então repeti-lo cinco vezes ou esticava o menu ou
           cortava justamente o pedaço que distingue as conexões. O gestor não
           recebe estes itens (opera pelas Configurações), como antes. -->
      <template v-if="!isGestor">
        <template v-for="(c, i) in conexoesIndividuaisView">
          <v-divider v-if="i > 0 || canGoToConfig" :key="`sep-${c.id}`" />

          <v-list-item :key="`status-${c.id}`" disabled class="status-row">
            <v-list-item-title class="d-flex align-center">
              <span
                v-if="c.nomeExibido"
                class="az-text-small font-weight-bold text-break mr-2"
                data-test="menu-conexao-nome"
              >
                {{ c.nomeExibido }}
              </span>
              <BaseStatus
                text
                :content="c.statusLabel"
                :color="c.statusColor"
              />
            </v-list-item-title>
          </v-list-item>

          <v-divider :key="`div-${c.id}`" />

          <v-list-item
            v-if="!c.conectado"
            :key="`qrcode-${c.id}`"
            data-test="menu-qrcode"
            @click="openQrCode(c)"
          >
            <v-list-item-icon>
              <icon-svg :name="icons.qrcode" size="16" color="#8e8e8e" />
            </v-list-item-icon>
            <v-list-item-title>Conectar via QR Code</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="c.conectado"
            :key="`reiniciar-${c.id}`"
            data-test="menu-reiniciar"
            @click="onReiniciar(c)"
          >
            <v-list-item-icon>
              <icon-svg :name="icons.refresh" size="16" color="#8e8e8e" />
            </v-list-item-icon>
            <v-list-item-title>Reiniciar conexão</v-list-item-title>
          </v-list-item>

          <v-list-item
            v-if="c.conectado"
            :key="`desconectar-${c.id}`"
            data-test="menu-desconectar"
            @click="onDesconectar(c)"
          >
            <v-list-item-icon>
              <icon-svg :name="icons.disconnect" size="16" color="#8e8e8e" />
            </v-list-item-icon>
            <v-list-item-title>Desconectar</v-list-item-title>
          </v-list-item>

          <v-list-item
            :key="`desativar-${c.id}`"
            data-test="menu-desativar"
            @click="onDesativar(c)"
          >
            <v-list-item-icon>
              <icon-svg :name="icons.deactivate" size="16" color="#8e8e8e" />
            </v-list-item-icon>
            <v-list-item-title>Desativar conexão</v-list-item-title>
          </v-list-item>

          <v-divider :key="`div-remover-${c.id}`" />

          <v-list-item
            :key="`remover-${c.id}`"
            data-test="menu-remover"
            @click="onRemoverPermanentemente(c)"
          >
            <v-list-item-icon>
              <icon-svg :name="icons.delete" size="16" color="#8e8e8e" />
            </v-list-item-icon>
            <v-list-item-title>Remover permanentemente</v-list-item-title>
          </v-list-item>
        </template>
      </template>
    </v-list>
  </v-menu>
</template>

<style lang="sass" scoped>
.status-row
  pointer-events: none

  // O cabeçalho é o ÚNICO lugar que nomeia a conexão depois que as ações
  // deixaram de repetir o nome: ele QUEBRA linha em vez de truncar, porque o
  // ellipsis padrão de `.v-list-item__title` cortaria justamente o pedaço que
  // diferencia dois blocos (nomes de 100 chars são aceitos no cadastro).
  ::v-deep .v-list-item__title
    white-space: normal
    overflow: visible
    text-overflow: clip
    line-height: 1.3
</style>

<style lang="sass">
// Conteúdo teletransportado do v-menu — `content-class` não recebe o atributo
// de escopo do componente, então esta regra precisa ser global (mesmo padrão
// de `.chat-actions-menu`/`.aawz-drive-folder-picker-menu`). Sem o teto de
// largura, um nome longo no cabeçalho estica o menu inteiro sobre a lista de
// conversas.
.conexao-actions-menu
  max-width: 320px
</style>
